import {
  ModelSetCharacteristics,
  ModelSetHolding,
  ModelSetPerformance,
} from 'features/premium-report/api/types';
import {
  AppendedPooCommentaries,
  PooCommentaries,
  UseCreatePooArguments,
} from '../components/PresentationOfOptions/useCreatePoo';
import { getRowsDisclosures } from 'features/premium-report/components/performance-disclosures';
import { filterOutPortfolioStorySlides } from '../components/SlidesEditor/portfolioStorySlideFilter';
// import { Strategy } from '../components/SlidesEditor/slides/FinalPortfolioStory'; // Not used - Final Portfolio Story is disabled

export const parseCommentariesToPoo = (
  data: UseCreatePooArguments,
  commentaries: PooCommentaries | AppendedPooCommentaries,
): any => {
  switch (data.type) {
    case 'initial':
      return parseCommentariesToInitialPoo(data, commentaries);
    case 'appended':
      return parseCommentariesToAppendedPoo(data, commentaries);
    case 'final':
      return parseCommentariesToFinalPoo(data, commentaries);
    case 'premium_report':
      return parseCommentariesToPremiumPoo(data, commentaries);
    default:
      return null;
  }
};
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const parseModelSetToPoo = (modelSet, portfolio_options): any => {
  const modifiedModelSet = {
    ...modelSet,
    portfolio_options: [portfolio_options],
  };
  return modifiedModelSet;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const parseCommentariesToInitialPoo = (
  data: UseCreatePooArguments,
  commentaries: any,
): any => {
  return {
    version: '1',
    type: data.type,
    commentariesData: commentaries.data,
    pptTemplateId: '188ruy32zRgsD2crwdkE098lp1fjmnPxtn4xbkIH03bs',
    slides: [...slidesInitial(commentaries, data)],
  };
};

const parseCommentariesToAppendedPoo = (
  data: UseCreatePooArguments,
  commentaries: any,
): any => {
  return {
    version: '1',
    type: data.type,
    commentariesData: commentaries.data,
    pptTemplateId: '188ruy32zRgsD2crwdkE098lp1fjmnPxtn4xbkIH03bs',
    slides: [...slidesAppended(commentaries, data)],
  };
};

const parseCommentariesToFinalPoo = (
  data: UseCreatePooArguments,
  commentaries: any,
): any => {
  return {
    version: '1',
    type: data.type,
    commentariesData: commentaries,
    pptTemplateId: '188ruy32zRgsD2crwdkE098lp1fjmnPxtn4xbkIH03bs',
    slides: [...slidesFinal(commentaries, data)],
  };
};

const parseCommentariesToPremiumPoo = (
  data: UseCreatePooArguments,
  commentaries: any,
): any => {
  const slides = slidesPremium(commentaries, data);
  const filteredSlides = filterOutPortfolioStorySlides(slides);

  return {
    version: 'premium',
    type: data.type,
    commentariesData: commentaries,
    pptTemplateId: '188ruy32zRgsD2crwdkE098lp1fjmnPxtn4xbkIH03bs',
    slides: filteredSlides,
  };
};

const slidesPremium = (commentaries: any, data: UseCreatePooArguments) => {
  let slideId = 1;
  const slides: any[] = [];

  slides.push(
    ...cover({
      ...commentaries,
      id: slideId,
      date: new Date().toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
      }),
      case: data,
    }),
  );
  slideId++;
  slides.push(
    ...tableOfContents({
      ...commentaries,
      id: slideId,
    }),
  );
  slideId++;

  slides.push(
    ...whatYouWantToAccomplish({
      ...commentaries,
      id: slideId,
    }),
  );
  slideId++;

  slides.push(
    ...guidingPhilosophy({
      ...commentaries,
      id: slideId,
    }),
  );
  slideId++;
  const modelSetHoldings =
    commentaries.generatedCommentaries.model_set_holdings ?? [];

  modelSetHoldings.forEach((modelSet, index) => {
    const modelSetPerformance =
      commentaries.generatedCommentaries.model_set_performance.filter(
        performance => performance.model_set_name === modelSet.model_set_name,
      );
    const modelSetCharacteristics =
      commentaries.generatedCommentaries.model_set_characteristics.filter(
        characteristic =>
          characteristic.model_set_name === modelSet.model_set_name,
      );
    slides.push(
      ...explanationOfModels({
        id: slideId,
        modelSetHoldings: modelSet,
      }),
    );
    slideId++;
    const rows = getRows(modelSet, index);
    const chunks = chunkArray(rows, 6);
    chunks.map(chunk => {
      slides.push(
        ...strategySelection({
          id: slideId,
          holdings: [{ holdings: chunk }],
        }),
      );
      slideId++;
    });
    let profile = 3;
    let profileIndex = 3;
    const rowsDisclosures = getRowsDisclosures(modelSetPerformance, modelSet);
    const chunksDisclosures = chunkArray(rowsDisclosures, 7);
    const excess_return_analysis =
      modelSetPerformance
        .map(performance => {
          profileIndex = performance.portfolio_options[0].portfolios.findIndex(
            portfolio => portfolio.excess_return_analysis,
          );
          profile =
            performance.portfolio_options[0].portfolios[profileIndex].profile;
          return profile >= 0
            ? performance.portfolio_options[0].portfolios[profileIndex]
                .excess_return_analysis
            : null;
        })
        .filter(analysis => analysis)[0] ?? '';

    chunksDisclosures.map((chunk, index) => {
      slides.push(
        ...performanceDisclosures({
          id: slideId,
          rows: chunk,
          key: `performance-disclosures-${slideId}-${index}`,
          modelSetHoldings: modelSetPerformance,
          profile: profile,
        }),
      );
      slideId++;
    });

    slides.push(
      ...excessReturnPerformance({
        id: slideId,
        modelSetPerformance: modelSetPerformance,
        excessReturnAnalysis: excess_return_analysis,
        profile: profile,
      }),
    );
    slideId++;
    const risk_analysis =
      modelSetPerformance
        .map(performance => {
          profileIndex = performance.portfolio_options[0].portfolios.findIndex(
            portfolio => portfolio.risk_analysis,
          );
          profile =
            performance.portfolio_options[0].portfolios[profileIndex].profile;
          return profile >= 0
            ? performance.portfolio_options[0].portfolios[profileIndex]
                .risk_analysis
            : null;
        })
        .filter(analysis => analysis)[0] ?? '';

    slides.push(
      ...riskAnalysis({
        id: slideId,
        modelSetPerformance: modelSetPerformance,
        riskAnalysis: risk_analysis,
        profile: profile,
      }),
    );
    slideId++;
    [1, 2].map(type => {
      slides.push(
        ...portfolioCharacteristics({
          id: slideId,
          modelSetCharacteristics: modelSetCharacteristics,
          type: type.toString(),
        }),
      );
      slideId++;
    });
    // Portfolio Story Slide - included in Premium Reports
    /**
     * Ticket: https://magnifi-dev.atlassian.net/browse/SAGE-3441
     * Description: Remove the Story Template slides from "Premium Reports"
     */
    // const storySlideIds = [20, 27]; // Portfolio Story and Final Portfolio Story
    // const text = modelSet.portfolio_story_premium_report ?? '';
    // if (text) {
    //   slides.push(
    //     ...portfolioStory({
    //       id: slideId,
    //       model_name: modelSet.model_set_name,
    //       text: text,
    //     }),
    //   );
    //   slideId++;
    // }
  });

  slides.push(
    ...amkProfiles({
      id: slideId,
    }),
  );
  slideId++;
  slides.push(
    ...amkProfiles2({
      id: slideId,
    }),
  );
  slideId++;
  slides.push(
    ...disclosures({
      id: slideId,
    }),
  );
  slideId++;
  return filterOutPortfolioStorySlides(slides);
};

const getRows = (modelSet: ModelSetHolding, index: number) => {
  let rowId = 0;
  if (
    !modelSet ||
    !Array.isArray(modelSet.portfolio_options) ||
    modelSet.portfolio_options.length === 0
  ) {
    return [];
  }
  return modelSet.portfolio_options.flatMap(option => {
    const allHoldings = option.portfolios.flatMap(
      (portfolio: any) => portfolio.holdings,
    );

    const uniqueHoldings = allHoldings.reduce((acc: any[], current: any) => {
      const exists = acc.find(
        newHolding =>
          newHolding.model_id === current.model_id ||
          newHolding.strategy === current.strategy,
      );
      if (!exists) {
        acc.push({
          ...current,
          id: `${index}-${rowId++}`,
          key: `${index}-${rowId++}`,
        });
      }
      return acc;
    }, []);

    return uniqueHoldings;
  });
};

const slidesFinal = (commentaries: any, data: UseCreatePooArguments) => {
  let slideId = 1;
  const slides: any[] = [];

  slides.push(
    ...slide1({
      ...commentaries,
      id: slideId,
      date: new Date().toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
      }),
      caseName: data.caseName,
    }),
  );
  slideId++;
  slides.push(
    ...slide4({
      ...commentaries,
      id: slideId,
      model_set_name: 'Final Model Selection',
      date: new Date().toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
      }),
    }),
  );
  slideId++;
  slides.push(
    ...slide10({
      ...commentaries,
      id: slideId,
      model_selected: commentaries.generatedCommentaries.model_selected,
    }),
  );
  slideId++;

  commentaries.generatedCommentaries.model_set_holdings.forEach((item: any) => {
    item.portfolio_options.forEach((option: any) => {
      slides.push(
        ...slide5({
          id: slideId,
          model_set_holdings: item,
          strategist_mix: option.strategist_mix,
          portfolio_options: option,
          option: option.option,
        }),
      );
      slideId++;
    });
    /**
     * Temporarily Hiding Final Portfolio Story Slide as per SAGE-3277
     * (https://magnifi-dev.atlassian.net/browse/SAGE-3277)
     */
    // const text = item.portfolio_story;
    // const strategies: Strategy[] = item.portfolio_options.flatMap(
    //   (option: any) =>
    //     option.portfolios.flatMap((portfolio: any) =>
    //       portfolio.holdings.map((holding: any) => ({
    //         category_role: holding.category_role,
    //         strategy: holding.strategy,
    //       })),
    //     ),
    // );
    // slides.push(
    //   ...finalPortfolioStory({
    //     id: slideId,
    //     model_name: item.model_set_name,
    //     text: text,
    //     strategies: strategies,
    //   }),
    // );
    slideId++;
  });
  if (commentaries.basePresentation === undefined) {
    slides.push(...slide9({ id: slideId }));
    slideId++;
  } else if (commentaries.basePresentation.type === 'initial') {
    slides.push(
      ...slide4({
        id: slideId,
        model_set_name: 'Initial Model Presentation',
        date: commentaries.basePresentation.slides[0].content[3].value,
      }),
    );
    slideId++;
  } /*else if (commentaries.basePresentation.type === 'appended') {
    slides.push(...slide10({ id: slideId }));
    slideId++;
  }*/
  if (commentaries.basePresentation !== undefined) {
    commentaries.basePresentation !== undefined &&
      commentaries.basePresentation.slides.slice(1).forEach((slide: any) => {
        slides.push({ ...slide, slideTemplateId: slideId });
        slideId++;
      });
  }
  return slides;
};

const slidesAppended = (commentaries: any, data: UseCreatePooArguments) => {
  let slideId = 1;
  const slides: any[] = [];

  slides.push(
    ...slide1({
      ...commentaries,
      id: slideId,
      date: new Date().toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
      }),
      caseName: data.caseName,
    }),
  );
  slideId++;
  slides.push(
    ...slide4({
      ...commentaries,
      id: slideId,
      model_set_name: 'Model Updates',
      date: new Date().toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
      }),
    }),
  );
  slideId++;
  slides.push(
    ...slide2({
      ...commentaries,
      id: slideId,
      title1: 'Model Update',
      title2: 'Models Updated',
      model_request: commentaries.generatedCommentaries.model_update,
      model_developed: commentaries.generatedCommentaries.models_updated,
    }),
  );
  slideId++;
  commentaries.generatedCommentaries.model_set_holdings.forEach((item: any) => {
    slides.push(
      ...slide4({ id: slideId, model_set_name: item.model_set_name, date: '' }),
    );
    slideId++;

    item.portfolio_options.forEach((option: any) => {
      slides.push(
        ...slide5({
          id: slideId,
          model_set_holdings: item,
          strategist_mix: option.strategist_mix,
          portfolio_options: option,
          option: option.option,
        }),
      );
      slideId++;

      const allHoldings = option.portfolios.flatMap(
        (portfolio: any) => portfolio.holdings,
      );
      const uniqueHoldings = allHoldings.reduce((acc: any[], current: any) => {
        const exists = acc.find(
          newHolding =>
            newHolding.model_id === current.model_id ||
            newHolding.strategy === current.strategy,
        );
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      const holdingsChunks = chunkArray(uniqueHoldings, 4);

      holdingsChunks.forEach(holdingsChunk => {
        slides.push(
          ...slide6({
            id: slideId,
            holdings: [{ holdings: holdingsChunk }],
            option: option.option,
          }),
        );
        slideId++;
      });
    });
    slides.push(
      ...slide7({
        id: slideId,
        model_set_holdings: item,
      }),
    );
    slideId++;
    slides.push(
      ...slide24({
        id: slideId,
        model_set_holdings: item,
      }),
    );
    slideId++;

    slides.push(
      ...slide8({
        id: slideId,
        model_differences: item.model_differences,
      }),
    );
    slideId++;
  });
  if (commentaries.basePresentation !== undefined) {
    slides.push(
      ...slide4({
        id: slideId,
        model_set_name: 'Initial Model Presentation',
        date: commentaries.basePresentation.slides[0].content[3].value,
      }),
    );
    slideId++;
    commentaries.basePresentation.slides.slice(1).forEach((slide: any) => {
      slides.push({ ...slide, slideTemplateId: slideId });
      slideId++;
    });
  }
  return slides;
};

const slidesInitial = (commentaries: any, data: UseCreatePooArguments) => {
  let slideId = 1;
  const slides: any[] = [];

  slides.push(
    ...slide1({
      id: slideId,
      date: new Date().toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
      }),
      caseName: data.caseName,
    }),
  );
  slideId++;

  slides.push(
    ...slide2({
      id: slideId,
      title1: 'Models Request',
      title2: 'Models Developed',
      model_request: commentaries.generatedCommentaries.model_request,
      model_developed: commentaries.generatedCommentaries.model_developed,
    }),
  );
  slideId++;
  slides.push(...slide3({ id: slideId }));
  slideId++;
  commentaries.generatedCommentaries.model_set_holdings.forEach((item: any) => {
    slides.push(
      ...slide4({ id: slideId, model_set_name: item.model_set_name }),
    );
    slideId++;

    item.portfolio_options.forEach((option: any) => {
      slides.push(
        ...slide5({
          id: slideId,
          model_set_holdings: item,
          strategist_mix: option.strategist_mix,
          portfolio_options: option,
          option: option.option,
        }),
      );
      slideId++;

      const allHoldings = option.portfolios.flatMap(
        (portfolio: any) => portfolio.holdings,
      );
      const uniqueHoldings = allHoldings.reduce((acc: any[], current: any) => {
        const exists = acc.find(
          newHolding =>
            newHolding.model_id === current.model_id ||
            newHolding.strategy === current.strategy,
        );
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      const holdingsChunks = chunkArray(uniqueHoldings, 4);

      holdingsChunks.forEach(holdingsChunk => {
        slides.push(
          ...slide6({
            id: slideId,
            holdings: [{ holdings: holdingsChunk }],
            option: option.option,
          }),
        );
        slideId++;
      });
    });
    slides.push(
      ...slide7({
        id: slideId,
        model_set_holdings: item,
      }),
    );
    slideId++;
    slides.push(
      ...slide24({
        id: slideId,
        model_set_holdings: item,
      }),
    );
    slideId++;

    slides.push(
      ...slide8({
        id: slideId,
        model_differences: item.model_differences,
      }),
    );
    slideId++;
  });
  slideId++;
  slides.push(
    ...slide25({
      id: slideId,
      title: 'Sample Model Differences',
      subtitle: 'Option 1 Details',
      subtitle2: 'Option 2 Details',
      bulletPointsOption1: `- Bullet Point 1\n- Bullet Point 2`,
      bulletPointsOption2: `- Bullet Point 1\n- Bullet Point 2`,
    }),
  );
  slideId++;
  slides.push(...slide9({ id: slideId }));
  return slides;
};

const cover = (data: any) => {
  return [
    {
      id: 10,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Investment Consulting',
          type: 'text',
        },
        {
          field: 'subtitle1',
          label: 'Subtitle 1',
          value: data.caseName,
          type: 'editableText',
        },
        {
          field: 'case',
          label: 'case',
          value: data.case,
          type: 'text',
        },
        {
          field: 'caseName',
          label: 'Case',
          value: data.case.caseName,
          type: 'text',
        },
        {
          field: 'owners1',
          label: 'Owners',
          value: data.case.owners[0],
          type: 'text',
        },
        {
          field: 'owners2',
          label: 'Owners',
          value: data.case.owners[1],
          type: 'text',
        },
        {
          field: 'date',
          label: 'Date',
          value: data.date,
          type: 'editableText',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: true,
          type: 'boolean',
        },
      ],
    },
  ];
};

const whatYouWantToAccomplish = (data: any) => {
  return [
    {
      id: 11,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'What You Want to Accomplish',
          type: 'text',
        },
        {
          field: 'modelSetHoldings',
          label: 'Model Set Holdings',
          value: data.generatedCommentaries.model_set_holdings,
          type: 'modelSetHoldings',
        },
        {
          field: 'specificConsiderations',
          label: 'Specific Considerations',
          value: data.generatedCommentaries.specific_considerations,
          type: 'editableTextarea',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: true,
          type: 'boolean',
        },
      ],
    },
  ];
};

const guidingPhilosophy = (data: any) => {
  return [
    {
      id: 12,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Guiding Philosophy, Goals & Principles',
          type: 'text',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: true,
          type: 'boolean',
        },
      ],
    },
  ];
};

const explanationOfModels = (data: {
  id: number;
  modelSetHoldings: ModelSetHolding;
}) => {
  return [
    {
      id: 13,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Explanation of Models',
          type: 'text',
        },
        {
          field: 'abbreviated_model_set_name',
          label: 'Abbreviated Model Set Name',
          value: data.modelSetHoldings.model_set_name,
          type: 'editableText',
        },
        {
          field: 'model_explaination',
          label: 'Model Explaination',
          value: data.modelSetHoldings.model_explaination,
          type: 'editableTextarea',
        },
        {
          field: 'modelSetHoldings',
          label: 'Model Set Holdings',
          value: data.modelSetHoldings,
          type: 'modelSetHoldings',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: true,
          type: 'boolean',
        },
      ],
    },
  ];
};

//TODO: Remove this if we're going to use strategy allocation on the explanation of models slide
// const strategyAllocation = (data: {
//   id: number;
//   modelSetHoldings: ModelSetHolding;
// }) => {
//   return [
//     {
//       id: 15,
//       slideTemplateId: data.id,
//       content: [
//         {
//           field: 'title',
//           label: 'Title',
//           value: 'Strategy Allocation',
//           type: 'text',
//         },
//         {
//           field: 'modelSetHoldings',
//           label: 'Model Set Holdings',
//           value: data.modelSetHoldings,
//           type: 'modelSetHoldings',
//         },
//         {
//           field: 'table_of_contents',
//           label: 'Table of Contents',
//           value: false,
//           type: 'boolean',
//         },
//       ],
//     },
//   ];
// };

const strategySelection = (data: { id: number; holdings: any[] }) => {
  return [
    {
      id: 14,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Strategy Selection',
          type: 'text',
        },
        {
          field: 'modelSetHoldings',
          label: 'Model Set Holdings',
          value: data.holdings,
          type: 'modelSetHoldings',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: false,
          type: 'boolean',
        },
      ],
    },
  ];
};

const performanceDisclosures = (data: {
  id: number;
  rows: any[];
  key: string;
  modelSetHoldings: any;
  profile: number;
}) => {
  return [
    {
      id: 16,
      key: data.key,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Back-tested Performance Results',
          type: 'text',
        },
        {
          field: 'rows',
          label: 'Rows',
          value: data.rows,
          type: 'rows',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: false,
          type: 'boolean',
        },
        {
          field: 'performance_disclosures',
          label: 'Performance Disclosures',
          value: data.modelSetHoldings,
          type: 'modelSetHoldings',
        },
        {
          field: 'profile',
          label: 'Profile',
          value: data.profile,
          type: 'number',
        },
      ],
    },
  ];
};

const excessReturnPerformance = (data: {
  id: number;
  modelSetPerformance: ModelSetPerformance[];
  excessReturnAnalysis: string;
  profile: number;
}) => {
  return [
    {
      id: 17,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Excess Return Analysis',
          type: 'text',
        },
        {
          field: 'excessReturnAnalysis',
          label: 'Excess Return Analysis',
          value: data.excessReturnAnalysis,
          type: 'text',
        },
        {
          field: 'modelSetPerformance',
          label: 'Model Set Performance',
          value: data.modelSetPerformance,
          type: 'modelSetPerformance',
        },
        {
          field: 'profile',
          label: 'Profile',
          value: data.profile,
          type: 'number',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: false,
          type: 'boolean',
        },
      ],
    },
  ];
};

const riskAnalysis = (data: {
  id: number;
  modelSetPerformance: ModelSetPerformance[];
  riskAnalysis: any[];
  profile: number;
}) => {
  return [
    {
      id: 18,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Risk Analysis',
          type: 'text',
        },
        {
          field: 'riskAnalysis',
          label: 'Risk Analysis',
          value: data.riskAnalysis,
          type: 'text',
        },
        {
          field: 'modelSetPerformance',
          label: 'Model Set Performance',
          value: data.modelSetPerformance,
          type: 'modelSetPerformance',
        },
        {
          field: 'profile',
          label: 'Profile',
          value: data.profile,
          type: 'number',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: false,
          type: 'boolean',
        },
      ],
    },
  ];
};

const portfolioCharacteristics = (data: {
  id: number;
  modelSetCharacteristics: ModelSetCharacteristics[];
  type: string;
}) => {
  return [
    {
      id: 19,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Portfolio Characteristics',
          type: 'text',
        },
        {
          field: 'modelSetCharacteristics',
          label: 'Model Set Characteristics',
          value: data.modelSetCharacteristics,
          type: 'modelSetCharacteristics',
        },
        {
          field: 'type',
          label: 'Type',
          value: data.type,
          type: 'string',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: false,
          type: 'boolean',
        },
      ],
    },
  ];
};

/**
 * Portfolio Story Slide for Premium Reports
 * Note: This slide is included in Premium Reports but hidden in Presentation of Options
 */

/**
 * Ticket: https://magnifi-dev.atlassian.net/browse/SAGE-3441
 * Description: Remove the Story Template slides from "Premium Reports"
 */

// const portfolioStory = (data: {
//   id: number;
//   model_name: string;
//   text: any;
// }) => {
//   // Parse text if it's an object, otherwise use as string
//   const storyText = typeof data.text === 'string' ? data.text : '';
//   const storyData = typeof data.text === 'object' ? data.text : {};

//   return [
//     {
//       id: 20,
//       slideTemplateId: data.id,
//       content: [
//         {
//           field: 'title',
//           label: 'Title',
//           value: `Portfolio Story - ${data.model_name}`,
//           type: 'text',
//         },
//         {
//           field: 'text',
//           label: 'Text',
//           value: storyText || data.model_name,
//           type: 'text',
//         },
//         {
//           field: 'backbone',
//           label: 'Backbone',
//           value: storyData.backbone || '',
//           type: 'text',
//         },
//         {
//           field: 'armsLegs',
//           label: 'Arms and Legs',
//           value: storyData.armsLegs || '',
//           type: 'text',
//         },
//         {
//           field: 'arms',
//           label: 'Arms',
//           value: storyData.arms || '',
//           type: 'text',
//         },
//         {
//           field: 'legs',
//           label: 'Legs',
//           value: storyData.legs || '',
//           type: 'text',
//         },
//         {
//           field: 'table_of_contents',
//           label: 'Table of Contents',
//           value: false,
//           type: 'boolean',
//         },
//       ],
//     },
//   ];
// };

/**
 * Temporarily Hiding Final Portfolio Story Slide as per SAGE-3277
 * (https://magnifi-dev.atlassian.net/browse/SAGE-3277)
 *
 * TODO: Remove the ESLint suppress rule once the Final Portfolio Story Slide is unhidden
 */

/**
 * Ticket: https://magnifi-dev.atlassian.net/browse/SAGE-3441
 * Description: Remove the Story Template slides from "Premium Reports"
 */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
// const finalPortfolioStory = (data: any) => {
//   return [
//     {
//       id: 27,
//       slideTemplateId: data.id,
//       content: [
//         {
//           field: 'title',
//           label: 'Title',
//           value: `One Simple Story for all portfolios`,
//           type: 'text',
//         },
//         {
//           field: 'text',
//           label: 'Text',
//           value: data.model_name,
//           type: 'text',
//         },
//         {
//           field: 'backbone',
//           label: 'Backbone',
//           value: data.text.backbone,
//           type: 'text',
//         },
//         {
//           field: 'arms',
//           label: 'Arms',
//           value: data.text.arms,
//           type: 'text',
//         },
//         {
//           field: 'legs',
//           label: 'Legs',
//           value: data.text.legs,
//           type: 'text',
//         },
//         {
//           field: 'strategies',
//           label: 'Strategies',
//           value: data.strategies,
//           type: 'strategies',
//         },
//       ],
//     },
//   ];
// };

const amkProfiles = (data: any) => {
  return [
    {
      id: 21,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'About AssetMark Investment Consulting',
          type: 'text',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: true,
          type: 'boolean',
        },
      ],
    },
  ];
};

const amkProfiles2 = (data: any) => {
  return [
    {
      id: 26,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'About AssetMark Investment Consulting',
          type: 'text',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: true,
          type: 'boolean',
        },
      ],
    },
  ];
};

const disclosures = (data: any) => {
  return [
    {
      id: 22,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Important Disclosures',
          type: 'text',
        },
        {
          field: 'table_of_contents',
          label: 'Table of Contents',
          value: true,
          type: 'boolean',
        },
      ],
    },
  ];
};

const tableOfContents = (data: any) => {
  return [
    {
      id: 23,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Table of Contents',
          type: 'text',
        },
        {
          field: 'slides',
          label: 'Slides',
          value: data.slides,
          type: 'slides',
        },
      ],
    },
  ];
};

const slide1 = (data: any) => {
  return [
    {
      id: 0,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Investment Consulting',
          type: 'text',
        },
        {
          field: 'subtitle1',
          label: 'Subtitle 1',
          value: data.caseName,
          type: 'editableText',
        },
        {
          field: 'subtitle2',
          label: 'Subtitle 2',
          value: 'Investment Portfolio Ideas',
          type: 'text',
        },
        {
          field: 'date',
          label: 'Date',
          value: data.date,
          type: 'editableText',
        },
      ],
    },
  ];
};

const slide2 = (data: any) => {
  return [
    {
      id: 1,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title1',
          label: 'Title 1',
          value: data.title1,
          type: 'editableTextarea',
        },
        {
          field: 'title2',
          label: 'Title 2',
          value: data.title2,
          type: 'editableTextarea',
        },
        {
          field: 'model_request',
          label: 'Model Request',
          value: data.model_request,
          type: 'editableTextarea',
        },
        {
          field: 'model_developed',
          label: 'Model Developed',
          value: data.model_developed,
          type: 'editableTextarea',
        },
      ],
    },
  ];
};

const slide3 = (data: any) => {
  return [
    {
      id: 2,
      slideTemplateId: data.id,
      content: [],
    },
  ];
};

const slide4 = (data: any) => {
  return [
    {
      id: 3,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: data.model_set_name,
          type: 'editableText',
        },
        {
          field: 'date',
          label: 'Date',
          value: data.date,
          type: 'editableText',
        },
      ],
    },
  ];
};

const slide5 = (data: any) => {
  return [
    {
      id: 4,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Option ' + data.option + ' - Strategist Mix',
          type: 'editableTextarea',
          // Default font size for title
          fontSize: '28px',
        },
        {
          field: 'subtitle1',
          label: 'CORE RETURN',
          value: data.strategist_mix,
          type: 'editableTextarea',
          // Default font size for subtitle1
          fontSize: '16px',
        },
        {
          field: 'modelSet',
          label: 'Model Set',
          value: parseModelSetToPoo(
            data.model_set_holdings,
            data.portfolio_options,
          ),
          type: 'modelSet',
        },
      ],
    },
  ];
};

const slide6 = (data: any) => {
  return [
    {
      id: 5,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Option ' + data.option + ' - Strategist Selection',
          type: 'editableTextarea',
        },
        {
          field: 'table',
          label: 'Table',
          value: data.holdings,
          type: 'editableTextarea',
        },
      ],
    },
  ];
};

const slide7 = (data: any) => {
  return [
    {
      id: 6,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Model Comparison',
          type: 'editableTextarea',
        },
        {
          field: 'modelSet',
          label: 'Model Set',
          value: data.model_set_holdings,
          type: 'modelSet',
        },
      ],
    },
  ];
};

const slide24 = (data: any) => {
  return [
    {
      id: 24,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Model Comparison - Fees',
          type: 'editableTextarea',
        },
        {
          field: 'modelSet',
          label: 'Model Set',
          value: data.model_set_holdings,
          type: 'modelSet',
        },
      ],
    },
  ];
};

const slide8 = (data: any) => {
  return [
    {
      id: 7,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Model Differences',
          type: 'editableTextarea',
        },
        {
          field: 'text',
          label: 'Text',
          value: data.model_differences,
          type: 'editableTextarea',
        },
      ],
    },
  ];
};

const slide9 = (data: any) => {
  return [
    {
      id: 8,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Important Information',
          type: 'simple',
        },
      ],
    },
  ];
};

const slide10 = (data: any) => {
  return [
    {
      id: 9,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Title',
          value: 'Model Selected',
          type: 'editableTextarea',
        },
        {
          field: 'model_selected',
          label: 'Model Selected',
          value: data.model_selected,
          type: 'editableTextarea',
        },
      ],
    },
  ];
};

const slide25 = (data: any) => {
  return [
    {
      id: 25,
      slideTemplateId: data.id,
      content: [
        {
          field: 'title',
          label: 'Sample Model Differences',
          value: data.title,
          type: 'editableTextarea',
        },
        {
          field: 'subtitle1',
          label: 'Sample text for differences',
          value: data.subtitle,
          type: 'editableTextarea',
        },
        {
          field: 'bulletPointsOption1',
          label: 'Bullet Points Option 1',
          value: data.bulletPointsOption1,
          type: 'editableTextarea',
        },
        {
          field: 'subtitle2',
          label: 'Subtitle 2',
          value: data.subtitle2,
          type: 'editableTextarea',
        },
        {
          field: 'bulletPointsOption2',
          label: 'Bullet Points Option 2',
          value: data.bulletPointsOption2,
          type: 'editableTextarea',
        },
      ],
    },
  ];
};

export const chunkArray = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};
